import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import { ChevronLeft, Search, Dumbbell, Cable, User, Cog } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageProvider";
import { fetchExercises, Exercise, localizeExerciseName } from "@/lib/fitnessApi";
import { translateMuscle } from "@/lib/muscleGroupTranslations";
import { translateEquipment } from "@/lib/equipmentTranslations";
import { useExerciseMediaBatch } from "@/hooks/useExerciseMedia";

// ── Equipment cards ─────────────────────────────────────────────────────────

const EQUIPMENT = [
  { key: "barbell",    bg: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=300&q=80" },
  { key: "dumbbell",   bg: "https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e?w=300&q=80" },
  { key: "cable",      bg: "https://images.unsplash.com/photo-1517963879433-6ad2b056d712?w=300&q=80" },
  { key: "kettlebell", bg: "https://images.unsplash.com/photo-1620188467120-5042ed1eb5da?w=300&q=80" },
  { key: "bodyweight", apiKey: "body weight",        bg: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=300&q=80" },
  { key: "machine",    apiKey: "leverage machine",   bg: "https://images.unsplash.com/photo-1540497077202-7c8a3999166f?w=300&q=80" },
  { key: "pullupbar",  apiKey: "body weight",        bg: "https://images.unsplash.com/photo-1598289431512-b97b0917affc?w=300&q=80" },
];

// Icon per equipment chip.
// - lucide-react where a reasonable match exists (Dumbbell, Cable, User, Cog).
// - Custom inline SVGs for barbell / kettlebell / pullupbar — lucide has no
//   gym-equipment match for those and we want a visual distinction from
//   Dumbbell so Barra ≠ Halteres at a glance.
const EQUIP_ICON_COLOR = "rgba(220,240,255,0.88)";
const EquipIcon: React.FC<{ slug: string }> = ({ slug }) => {
  const size = 22;
  const stroke = 1.8;
  switch (slug) {
    case "dumbbell":
      return <Dumbbell size={size} strokeWidth={stroke} color={EQUIP_ICON_COLOR} />;
    case "cable":
      return <Cable size={size} strokeWidth={stroke} color={EQUIP_ICON_COLOR} />;
    case "bodyweight":
      return <User size={size} strokeWidth={stroke} color={EQUIP_ICON_COLOR} />;
    case "machine":
      return <Cog size={size} strokeWidth={stroke} color={EQUIP_ICON_COLOR} />;
    case "barbell":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
          stroke={EQUIP_ICON_COLOR} strokeWidth={stroke}
          strokeLinecap="round" strokeLinejoin="round">
          <rect x="1.5" y="9" width="2.5" height="6" rx="0.5" />
          <rect x="20" y="9" width="2.5" height="6" rx="0.5" />
          <rect x="4.8" y="10.5" width="1.2" height="3" />
          <rect x="18" y="10.5" width="1.2" height="3" />
          <line x1="6.2" y1="12" x2="17.8" y2="12" />
        </svg>
      );
    case "kettlebell":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
          stroke={EQUIP_ICON_COLOR} strokeWidth={stroke}
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 4h6a1 1 0 0 1 1 1v1.2a1 1 0 0 1-.6.92A7 7 0 0 1 20 13v2a5.5 5.5 0 0 1-5.5 5.5h-5A5.5 5.5 0 0 1 4 15v-2a7 7 0 0 1 4.6-5.88A1 1 0 0 1 8 6.2V5a1 1 0 0 1 1-1z" />
        </svg>
      );
    case "pullupbar":
      return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
          stroke={EQUIP_ICON_COLOR} strokeWidth={stroke}
          strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 4v16" />
          <path d="M20 4v16" />
          <path d="M3 5h18" />
          <path d="M9.5 5v3" />
          <path d="M14.5 5v3" />
        </svg>
      );
    default:
      return <Dumbbell size={size} strokeWidth={stroke} color={EQUIP_ICON_COLOR} />;
  }
};

const MUSCLES = ["all", "chest", "back", "upper legs", "shoulders", "upper arms", "waist", "lower legs", "lower arms", "cardio"];
const MUSCLE_I18N: Record<string, string> = {
  all: "fitness.library.muscles.all",
  cardio: "fitness.muscle.cardio",
  chest: "fitness.muscle.chest",
  back: "fitness.muscle.back",
  "upper legs": "fitness.muscle.legs",
  shoulders: "fitness.muscle.shoulders",
  "upper arms": "fitness.muscle.arms",
  waist: "fitness.muscle.core",
  "lower legs": "fitness.muscle.calves",
  "lower arms": "fitness.muscle.forearms",
};

const MUSCLE_COLORS: Record<string, string> = {
  cardio: "hsl(187 72% 50%)",
  chest: "hsl(0 70% 55%)", back: "hsl(210 70% 55%)", "upper legs": "hsl(142 55% 50%)",
  shoulders: "hsl(38 90% 55%)", "upper arms": "hsl(265 60% 60%)", waist: "hsl(187 72% 50%)",
  "lower legs": "hsl(45 85% 55%)", "lower arms": "hsl(340 65% 55%)",
};

// ── Page ────────────────────────────────────────────────────────────────────

const FitnessLibrary: React.FC = () => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  const translateRef = useRef(t);
  translateRef.current = t;
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [equipFilter, setEquipFilter] = useState<string | null>(null);
  const [muscleFilter, setMuscleFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  // Tracks equipment slugs whose photo failed to load. When a slug is true,
  // the chip renders the cosmic-tech-bg + EquipIcon fallback instead of the
  // <img>. Updates via setter inside <img onError>.
  const [failedEquipImgs, setFailedEquipImgs] = useState<Record<string, boolean>>({});
  const stagger = (i: number) => ({ delay: 0.04 + i * 0.04, duration: 0.4 });
  const PAGE_SIZE = 20;

  // Batch-fetch video/media for visible exercises
  const exerciseNames = exercises.map(e => e.name);
  const fallbackGifs: Record<string, string> = {};
  for (const ex of exercises) { if (ex.gifUrl) fallbackGifs[ex.name] = ex.gifUrl; }
  const { mediaMap } = useExerciseMediaBatch(exerciseNames, fallbackGifs);

  useEffect(() => {
    // Each query owns its result. A late page/filter request cannot replace
    // the currently selected catalog, including during the search debounce.
    let current = true;
    setLoading(true);
    if (page === 1) setExercises([]);

    const timer = setTimeout(async () => {
      const bodyPart = muscleFilter === "all" ? undefined : muscleFilter;
      const equip = equipFilter || undefined;
      // Search the complete bounded catalog returned by this API, before
      // slicing the visible page. Searching the first 40 hid later matches.
      const data = await fetchExercises(bodyPart, equip, 200);
      if (!current) return;

      const normalize = (value: string) => value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().trim();
      const query = normalize(search);
      const translate = translateRef.current;
      const filtered = query ? data.filter(exercise =>
        [
          exercise.name, exercise.target, exercise.bodyPart, exercise.equipment,
          localizeExerciseName(exercise.name, translate, lang),
          translateMuscle(exercise.target, translate),
          translateMuscle(exercise.bodyPart, translate),
          translateEquipment(exercise.equipment, translate),
          ...EQUIPMENT.filter(option => (option.apiKey || option.key) === exercise.equipment.trim().toLowerCase())
            .slice(0, 1).map(option => translate("fitness.library.equipment." + option.key)),
        ].some(label => normalize(label).includes(query))
      ) : data;
      const visibleCount = PAGE_SIZE * page;
      setExercises(filtered.slice(0, visibleCount));
      setHasMore(filtered.length > visibleCount);
      setLoading(false);
    }, search.trim() ? 400 : 0);

    return () => {
      current = false;
      clearTimeout(timer);
    };
  }, [muscleFilter, equipFilter, search, page, lang]);

  const loadMore = () => {
    if (!loading && hasMore) setPage(current => current + 1);
  };

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }} />
      </div>

      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-muted-foreground mb-4 active:opacity-70">
        <ChevronLeft size={16} /><span className="text-[13px] font-display">{t("common.back")}</span>
      </button>

      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mb-5">
        <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5" style={{ color: "hsl(187 72% 50% / 0.55)" }}>
          {t("fitness.library.subtitle")}
        </p>
        <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">{t("fitness.library.title")}</h1>
      </motion.div>

      {/* EQUIPMENT CARDS — horizontal scroll */}
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={stagger(0)}
        className="mb-5 -mx-4 px-4">
        <div className="flex gap-2.5 overflow-x-auto pb-2" style={{ scrollbarWidth: "none" }}>
          {EQUIPMENT.map((eq) => {
            const active = equipFilter === (eq.apiKey || eq.key);
            const failed = !!failedEquipImgs[eq.key];
            // Active gets the pulse keyframes (animation owns box-shadow while
            // active; inline boxShadow not set for active to avoid conflict).
            // When image failed, the chip picks up cosmic-tech-bg so the
            // EquipIcon has a proper backdrop.
            const className =
              "shrink-0 w-[100px] h-[72px] rounded-2xl relative overflow-hidden transition-all active:scale-[0.96] hover:scale-[1.02]" +
              (active ? " equipment-chip-pulse" : "") +
              (failed ? " cosmic-tech-bg" : "");
            return (
              <button key={eq.key}
                onClick={() => { setPage(1); setEquipFilter(active ? null : (eq.apiKey || eq.key)); }}
                className={className}
                style={{
                  border: active
                    ? "1.5px solid hsl(187 72% 50% / 0.80)"
                    : "1px solid rgba(0,229,255,0.18)",
                }}>
                {failed ? (
                  // Fallback — same visual as pre-photo redesign: cosmic-tech
                  // backdrop + centered glyph. Offset upward so the label slot
                  // stays clear.
                  <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ paddingBottom: 14 }}>
                    <EquipIcon slug={eq.key} />
                  </div>
                ) : (
                  <img
                    src={eq.bg}
                    alt=""
                    draggable={false}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover pointer-events-none"
                    style={{ filter: "grayscale(80%) brightness(0.7) contrast(1.1)" }}
                    onError={() =>
                      setFailedEquipImgs((prev) => ({ ...prev, [eq.key]: true }))
                    }
                  />
                )}
                {/* Bottom fade so the label keeps AA contrast over either backdrop */}
                <div className="absolute bottom-0 left-0 right-0 h-7 pointer-events-none"
                  style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.72))" }} />
                <span className="absolute bottom-2 left-0 right-0 text-center font-display text-[9px] font-bold text-white tracking-wide" style={{ zIndex: 2 }}>
                  {t(`fitness.library.equipment.${eq.key}`)}
                </span>
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* MUSCLE FILTERS */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={stagger(1)}
        className="flex gap-1.5 overflow-x-auto pb-2 mb-4 -mx-4 px-4" style={{ scrollbarWidth: "none" }}>
        {MUSCLES.map(m => {
          const active = muscleFilter === m;
          return (
            <button key={m} onClick={() => { setPage(1); setMuscleFilter(m); }}
              className="shrink-0 px-3 py-1.5 rounded-full font-display text-[10px] font-medium transition-all"
              style={{
                background: active ? "hsl(187 72% 50% / 0.15)" : "transparent",
                border: `1px solid ${active ? "hsl(187 72% 50% / 0.45)" : "hsl(187 72% 50% / 0.15)"}`,
                color: active ? "hsl(187 72% 55%)" : "hsl(218 14% 55%)",
              }}>
              {t(MUSCLE_I18N[m] || `fitness.muscle.${m}`)}
            </button>
          );
        })}
      </motion.div>

      {/* SEARCH */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={stagger(2)}
        className="relative mb-5">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "hsl(218 14% 40%)" }} />
        <input
          value={search}
          onChange={(e) => { setPage(1); setSearch(e.target.value); }}
          placeholder={t("fitness.library.searchPlaceholder")}
          className="w-full pl-9 pr-4 py-2.5 rounded-xl text-[13px] font-body text-white placeholder:text-muted-foreground outline-none"
          style={{ background: "hsl(218 14% 12%)", border: "1px solid hsl(218 14% 20%)" }}
        />
      </motion.div>

      {/* EXERCISE GRID */}
      {loading && exercises.length === 0 ? (
        <div className="grid grid-cols-2 gap-2.5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[160px] rounded-2xl animate-pulse" style={{ background: "hsl(218 14% 12%)" }} />
          ))}
        </div>
      ) : exercises.length === 0 ? (
        <div className="flex flex-col items-center py-16 text-center">
          <Dumbbell size={32} style={{ color: "hsl(218 14% 30%)" }} />
          <p className="text-[13px] font-display text-foreground/50 mt-3">{t("fitness.library.noResults")}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2.5 mb-4">
            {exercises.map((ex, i) => {
              const color = MUSCLE_COLORS[ex.bodyPart] || "hsl(187 72% 50%)";
              return (
                <motion.button key={ex.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03, duration: 0.35 }}
                  onClick={() => navigate(`/fitness/library/${ex.id}`)}
                  className="rounded-2xl relative overflow-hidden text-left transition-all active:scale-[0.97] hover:scale-[1.02] hover:brightness-110 flex flex-col"
                  style={{
                    border: "1px solid rgba(0,229,255,0.15)",
                    background: "hsl(218 14% 10%)",
                  }}>
                  {/* Video / GIF / Placeholder */}
                  <div className="w-full relative" style={{ height: 110 }}>
                    {(() => {
                      const m = mediaMap[ex.name];
                      const videoUrl = m?.videoUrl;
                      const gifUrl = m?.gifUrl || ex.gifUrl;
                      const thumbUrl = m?.thumbnailUrl;

                      if (videoUrl) {
                        return (
                          <video
                            src={videoUrl}
                            poster={thumbUrl || undefined}
                            autoPlay muted loop playsInline
                            className="w-full h-full object-cover"
                          />
                        );
                      }
                      if (gifUrl) {
                        return (
                          <img src={gifUrl} alt={ex.name} loading="lazy"
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }} />
                        );
                      }
                      return (
                        <div className="w-full h-full flex items-center justify-center" style={{ background: "hsl(218 14% 12%)" }}>
                          <Dumbbell size={24} style={{ color: "hsl(218 14% 25%)" }} />
                        </div>
                      );
                    })()}
                    <div className="absolute inset-0" style={{ background: "linear-gradient(transparent 50%, rgba(0,0,0,0.60) 100%)" }} />
                  </div>
                  {/* Info */}
                  <div className="p-2.5 flex-1 flex flex-col justify-between">
                    <p className="font-display text-[10px] font-bold text-white leading-tight mb-1.5 line-clamp-2">{localizeExerciseName(ex.name, t, lang)}</p>
                    <div className="flex gap-1 flex-wrap">
                      <span className="text-[7px] font-display font-bold px-1.5 py-0.5 rounded-md"
                        style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}>
                        {translateMuscle(ex.target, t)}
                      </span>
                      <span className="text-[7px] font-display px-1.5 py-0.5 rounded-md"
                        style={{ background: "hsl(218 14% 18%)", color: "hsl(218 14% 55%)", border: "1px solid hsl(218 14% 25%)" }}>
                        {translateEquipment(ex.equipment, t)}
                      </span>
                    </div>
                  </div>
                </motion.button>
              );
            })}
          </div>

          {hasMore && (
            <button onClick={loadMore} disabled={loading}
              className="w-full py-3 rounded-xl font-display text-[12px] font-medium mb-4 active:scale-[0.97]"
              style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.20)", color: "hsl(187 72% 55%)" }}>
              {t("fitness.library.loadMore")}
            </button>
          )}
        </>
      )}

    </MobileLayout>
  );
};

export default FitnessLibrary;
